import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Skeleton, Alert, Button } from '@heroui/react';
import ResourcesPage from './ResourcesPage.js';
import ViewAircraftPage from './ViewAircraftPage.js';
import DiscrepanciesPage from './DiscrepanciesPage.js';
import InspectionsPage from './InspectionsPage.js';
import MetersPage from './MetersPage.js';
import WeightBalancePage from './WeightBalancePage.js';
import EnvelopePage from './EnvelopePage.js';
import FuelTankPage from './FuelTankPage.js';
import ViewSimulatorPage from './ViewSimulatorPage.js';
import SimulatorMetersPage from './SimulatorMetersPage.js';
import ResourceMetersPage from './ResourceMetersPage.js';
import BaseApi from '../../api/BaseApi.js';
import { normalizeAccessGroups } from '../../utils/normalizeAccessGroups.js';
import { useUserFormatsVersion } from '../common/user-formats/userFormats.js';

// Access groups context for permission gates across all Resources routes
const AccessGroupsContext = React.createContext({});

export const useAccessGroups = () => React.useContext(AccessGroupsContext);

/**
 * How many times the access-groups request is attempted before the page gives
 * up, and how long it waits between tries.
 *
 * A retry is not defensive padding here — it is the fix. The permissions walk
 * behind `/dashboard` is heavy for users whose roles carry duplicated
 * `roles_access_groups_rels` rows (measured on dev: 8,745 rows and 308–1,087 ms
 * for one such user, on a query that runs for every authenticated API call), so
 * a request lands on the slow tail often enough to fail intermittently. Reloading
 * the page fixed it, which is exactly what this does without the user's help.
 */
const FETCH_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [500, 1500];

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Shown above the subtree when the access-groups request has failed. The subtree
// still renders alongside it — the backend serves the page for reading, and every
// per-site gate already reads `_loaded && <group>`, so an unset group fails closed
// on its own. This is only telling the user why actions vanished and offering the
// retry the September fix intended.
function AccessGroupsFailedBanner({ onRetry }) {
	return (
		<div className="max-w-[1400px] mx-auto px-4 pt-8">
			<Alert
				color="warning"
				variant="flat"
				radius="sm"
				title="Could not load your permissions"
				endContent={
					<Button size="sm" variant="flat" color="warning" onPress={onRetry}>
						Try again
					</Button>
				}
			>
				Actions are hidden until they load. This is not a change to your access.
			</Alert>
		</div>
	);
}

// Shown in place of the subtree until the access-groups answer is in. Mirrors the
// skeleton shape the pages under these routers use for their own loading state.
function AccessGroupsPending() {
	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8" aria-busy="true" aria-live="polite">
			<span className="sr-only">Loading permissions</span>
			<div className="flex flex-col gap-4">
				<Skeleton className="h-24 w-full rounded-lg" />
				<Skeleton className="h-64 w-full rounded-lg" />
				<Skeleton className="h-48 w-full rounded-lg" />
			</div>
		</div>
	);
}

/**
 * Holds the access groups for everything below it, and — when it owns the
 * request — withholds the subtree until that request has answered, so a page
 * never paints its actions from an empty permission set.
 *
 * The gate keys on ownership, never on an opt-in prop: a page added under one of
 * these routers later inherits it with no extra code.
 *
 * ⛔ Controlled mode must never gate. A controlled caller supplies `value` from
 * its own bootstrap, and that bootstrap is dispatched from inside the page
 * component (the four Calendar V2 entrypoints). Withholding the children there
 * means the page never mounts, the bootstrap never runs, `value._loaded` never
 * turns true and the loader never ends — a hang, not a slow paint. Those pages
 * already render their own loading and error screens.
 *
 * `fallback` only replaces the pending element; it does not decide whether to
 * gate.
 */
export function AccessGroupsProvider({ children, value, fallback }) {
	// Controlled mode: when a caller passes `value`, it owns the access-groups
	// payload and this provider skips its own `/org/{org}/dashboard` fetch. The
	// Calendar Month V2 page uses this so the bootstrap thunk's single dashboard
	// request supplies the access groups instead of duplicating the call here.
	// Every other call-site passes no `value` → unchanged self-fetch behavior.
	const isControlled = value != null;
	const [accessGroups, setAccessGroups] = React.useState({ _loaded: false });
	// Bumping this re-runs the effect — the manual "Try again" path.
	const [attemptId, setAttemptId] = React.useState(0);

	const retry = React.useCallback(() => {
		setAccessGroups({ _loaded: false });
		setAttemptId(n => n + 1);
	}, []);

	React.useEffect(() => {
		if (isControlled) return undefined;

		let cancelled = false;

		const load = async () => {
			let lastError = null;

			for (let attempt = 0; attempt < FETCH_ATTEMPTS; attempt += 1) {
				if (attempt > 0) {
					await wait(RETRY_DELAYS_MS[attempt - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]);
					if (cancelled) return;
				}

				try {
					const endpoint = BaseApi.buildOrgEndpoint('/dashboard');
					const response = await BaseApi.get(endpoint);
					if (cancelled) return;
					const groups = response.data?.data?.accessGroups || [];
					setAccessGroups({ ...normalizeAccessGroups(groups), _loaded: true, _failed: false });
					return;
				} catch (err) {
					lastError = err;
					// A 403 is an answer, not an outage: this caller may not read the
					// dashboard at all, and retrying cannot change that.
					if (err?.response?.status === 403) break;
				}
			}

			if (cancelled) return;
			console.error('Failed to load access groups:', lastError);
			// `_failed` is what separates "we could not read your permissions" from
			// "you have none". Both still hide every action — failing closed is the
			// only safe default — but only one of them is worth telling the user
			// about, and only one is worth offering a retry for.
			setAccessGroups({ _loaded: true, _failed: true });
		};

		load();

		return () => {
			cancelled = true;
		};
	}, [isControlled, attemptId]);

	const contextValue = React.useMemo(
		() => (isControlled ? value : { ...accessGroups, _retry: retry }),
		[isControlled, value, accessGroups, retry]
	);

	// Gate on ownership alone. `fallback` only picks what is shown meanwhile, so it
	// can be left out — or set to null — without reopening the gate.
	const isPending = !isControlled && !accessGroups._loaded;
	const pendingElement = fallback === undefined ? <AccessGroupsPending /> : fallback;
	// A failed request is an answer, not a pending state: it never gates. The
	// subtree still renders for reading; this banner only sits above it to explain
	// why every action is hidden and to offer the retry.
	const showFailureBanner = !isControlled && accessGroups._loaded && accessGroups._failed;

	return (
		<AccessGroupsContext.Provider value={contextValue}>
			{isPending ? (
				pendingElement
			) : (
				<>
					{showFailureBanner && <AccessGroupsFailedBanner onRetry={retry} />}
					{children}
				</>
			)}
		</AccessGroupsContext.Provider>
	);
}

// Get dynamic basename for React Router (org-aware)
const getBasePath = () => {
	const match = window.location.pathname.match(/\/org\/[^/]+/);
	return match ? `${match[0]}/resources` : '/resources';
};

function ResourcesRouter() {
	// Pages format dates during render, so re-render them when the user formats arrive.
	useUserFormatsVersion();
	return (
		<BrowserRouter basename={getBasePath()}>
			<AccessGroupsProvider>
				<Routes>
					{/* Main resources list */}
					<Route path="/" element={<ResourcesPage />} />

					{/* Aircraft routes */}
					<Route path="/aircraft/:aircraftId" element={<ViewAircraftPage />} />
					<Route path="/aircraft/:aircraftId/discrepancies" element={<DiscrepanciesPage />} />

					{/* Aircraft sub-pages */}
					<Route path="/aircraft/:aircraftId/inspections" element={<InspectionsPage />} />
					<Route path="/aircraft/:aircraftId/meters" element={<MetersPage />} />

					{/* Weight & Balance */}
					<Route path="/aircraft/:aircraftId/weight-balance" element={<WeightBalancePage />} />

					{/* CG Envelope */}
					<Route path="/aircraft/:aircraftId/envelope" element={<EnvelopePage />} />

					{/* Fuel Tank */}
					<Route path="/aircraft/:aircraftId/fuel-tank" element={<FuelTankPage />} />

					{/* Simulator routes */}
					<Route path="/simulator/:simulatorId" element={<ViewSimulatorPage />} />
					<Route path="/simulator/:simulatorId/meters" element={<SimulatorMetersPage />} />

					{/* Generic resource routes (for instructors, rooms, equipment, etc.) */}
					<Route path="/resource/:resourceId/meters" element={<ResourceMetersPage />} />

				</Routes>
			</AccessGroupsProvider>
		</BrowserRouter>
	);
}

export default ResourcesRouter;
